import React, { createContext, useState, useEffect, useContext } from 'react';
import { API_ENDPOINTS } from '../utils/config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    // 1. Check LocalStorage on App Startup
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        // Removed guest_mode logic since tokens are now consistent

        if (storedToken && storedUser) {
            setUser(JSON.parse(storedUser));
            setToken(storedToken);
        }
        setLoading(false);
    }, []);

    // 2. Login Action
    const login = (userData, authToken) => {
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', authToken);
    };

    // 3. Guest Action (Async)
    const enterGuest = async () => {
        try {
            const url = API_ENDPOINTS.GUEST_LOGIN;
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });
            let data = {};
            try {
                data = await res.json();
            } catch (_) {
                /* non-JSON body */
            }

            if (res.ok && data.token) {
                // Use standard login to set user/token
                login(data.user, data.token);
                // Return token/user so callers (e.g. arcade finalize) can submit
                // immediately without waiting for a React re-render.
                return { ok: true, token: data.token, user: data.user };
            }
            const message = data.error || `Guest login failed (HTTP ${res.status})`;
            console.error("Guest login failed:", message, url);
            return { ok: false, error: message };
        } catch (err) {
            // Usually CORS/network when the browser cannot reach :5005/:8080
            console.error("Guest login error:", err, API_ENDPOINTS.GUEST_LOGIN);
            return {
                ok: false,
                error: `Cannot reach auth at ${API_ENDPOINTS.GUEST_LOGIN}. Restart npm start (needs setupProxy) or open via the forwarded :3000 URL.`,
            };
        }
    };

    // 4. Logout Action
    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('guest_mode');
        window.location.href = '/'; // Hard redirect to clear state completely
    };

    return (
        <AuthContext.Provider value={{ user, token, login, enterGuest, logout, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);